import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { multiFactor } from 'firebase/auth';

import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';
import { LoggingService } from '@core/services/logging.service';
import { RuntimeConfigurationService } from '@core/services/runtime-configuration.service';

/**
 * mfaEnrolledGuard
 *
 * Enforces the `security.mfaEnforcement` runtime-configuration policy on
 * protected dashboard routes. Intended to run AFTER adminAuthGuard (which
 * guarantees an authenticated, hydrated session), mirroring the canActivate
 * array pattern used across the platform.
 *
 * Behavior by mode:
 * - 'off' / 'prompt': always passes ('prompt' is handled by the dismissible
 *   banner in the admin layout, not by blocking navigation).
 * - 'required': users with a verified email and zero enrolled second factors
 *   are redirected to /enroll-mfa. Unverified users pass — Firebase cannot
 *   enroll an unverified account; the email-verification flow handles them.
 *
 * SSR: passes on the server (Firebase Auth state is browser-only).
 * Config failures fail-open (policy read is best-effort; auth itself is
 * still enforced by adminAuthGuard).
 */
export const mfaEnrolledGuard: CanActivateFn = async () => {
  const platformId = inject(PLATFORM_ID);
  const runtimeConfiguration = inject(RuntimeConfigurationService);
  const firebaseHelper = inject(FirebaseInjectionHelperService);
  const loggingService = inject(LoggingService);
  const router = inject(Router);

  // SSR: Firebase Auth (and the enforcement decision) is browser-only.
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  let enforcement = runtimeConfiguration.getMfaEnforcement();
  if (!runtimeConfiguration.isConfigurationLoaded()) {
    try {
      await runtimeConfiguration.waitForConfiguration();
      enforcement = runtimeConfiguration.getMfaEnforcement();
    } catch (error) {
      // Fail-open: without a policy we cannot require enrollment. Auth is
      // still enforced by adminAuthGuard ahead of this guard.
      loggingService.logWarning(
        'mfaEnrolledGuard: configuration unavailable, defaulting to off',
        { error },
      );
      return true;
    }
  }

  if (enforcement !== 'required') {
    return true;
  }

  const firebaseUser = firebaseHelper.getAuth().currentUser;
  if (!firebaseUser) {
    // adminAuthGuard owns the unauthenticated redirect; nothing to enforce.
    return true;
  }

  // Firebase rejects MFA enrollment for unverified emails — let the
  // verification banner run first (#943: verification no longer blocks).
  if (!firebaseUser.emailVerified) {
    return true;
  }

  if (multiFactor(firebaseUser).enrolledFactors.length === 0) {
    return router.createUrlTree(['/enroll-mfa']);
  }

  return true;
};
